import { z } from "zod";
import { CompositionAspectRatioDto } from "./aspect-ratio.ts";

export const VideoRenderStatus = z.enum([
  "pending",
  "rendering",
  "upscaling",
  "done",
  "failed",
]);

export const VideoRenderUpscaleQuality = z.enum(["1080p", "4k"]);

export const VideoRenderUpscaleProviderType = z.enum([
  "amplifiles/upscaler-1.0",
  "amplifiles/upscaler-1.1",
]);

export const VideoRenderUpscaleInfo = z.object({
  requestId: z.string(),
});

export const VideoRenderThirdPartyInfo = z.object({
  status: z.enum(["done", "failed"]),
});

export const VideoRenderVimeoStatusPending = z.literal("pending");
export const VideoRenderVimeoStatusDone = z.literal("done");
export const VideoRenderVimeoStatusFailed = z.literal("failed");
export const VideoRenderVimeoStatusDeleted = z.literal("deleted");
export const VideoRenderVimeoStatus = z.union([
  VideoRenderVimeoStatusPending,
  VideoRenderVimeoStatusDone,
  VideoRenderVimeoStatusFailed,
  VideoRenderVimeoStatusDeleted,
]);

export const VideoRenderVimeoInfo = z.union([
  z.object({
    status: z.union([
      VideoRenderVimeoStatusDone,
      VideoRenderVimeoStatusDeleted,
    ]),
    uri: z.string(),
  }),
  z.object({
    status: z.union([
      VideoRenderVimeoStatusFailed,
      VideoRenderVimeoStatusPending,
    ]),
  }),
]);

export const VideoRenderShareStatus = z.enum([
  "pending",
  "done",
  "failed",
  "deleted",
]);

export const VideoRenderShareInfo = z.union([
  z.object({
    status: z.union([z.literal("done"), z.literal("deleted")]),
    key: z.string(),
  }),
  z.object({
    status: z.union([z.literal("pending"), z.literal("failed")]),
  }),
]);

export const VideoRenderLocalInfo = z.object({
  type: z.literal("local"),
});

export const VideoRenderRemoteInfo = z.object({
  type: z.literal("remote"),
  renderId: z.string(),
  bucketName: z.string(),
  functionName: z.string(),
});

export const VideoRenderRenderInfo = z.union([
  VideoRenderLocalInfo,
  VideoRenderRemoteInfo,
]);

export const VideoRenderExportType = z.enum(["DOWNLOAD", "LINK", "VIMEO"]);

export const CreateVideoRenderDto = z.object({
  externalFileName: z.string(),
  exportType: VideoRenderExportType,
  upscaleQuality: VideoRenderUpscaleQuality.nullable(),
});

export const DeleteVideoRenderFromVimeoResponseDto = z.object({
  success: z.boolean(),
  externalId: z.string(),
});

export const VideoRenderDto = z.object({
  externalId: z.string(),
  externalFileName: z.string(),
  createdAt: z.string(),
  status: VideoRenderStatus,
  vimeoInfo: VideoRenderVimeoInfo.nullable(),
  shareInfo: VideoRenderShareInfo.nullable(),
  shareExternalId: z.string(),
  shareUrl: z.string().nullable(),
  downloadUrl: z.string().nullable(),
  upscaleQuality: VideoRenderUpscaleQuality.nullable(),
  aspectRatio: CompositionAspectRatioDto.nullable(),
});

export type VideoRenderStatus = z.infer<typeof VideoRenderStatus>;
export type VideoRenderUpscaleQuality = z.infer<
  typeof VideoRenderUpscaleQuality
>;
export type VideoRenderUpscaleProviderType = z.infer<
  typeof VideoRenderUpscaleProviderType
>;
export type VideoRenderUpscaleInfo = z.infer<typeof VideoRenderUpscaleInfo>;
export type VideoRenderThirdPartyInfo = z.infer<
  typeof VideoRenderThirdPartyInfo
>;
export type VideoRenderVimeoStatusPending = z.infer<
  typeof VideoRenderVimeoStatusPending
>;
export type VideoRenderVimeoStatusDone = z.infer<
  typeof VideoRenderVimeoStatusDone
>;
export type VideoRenderVimeoStatusFailed = z.infer<
  typeof VideoRenderVimeoStatusFailed
>;
export type VideoRenderVimeoStatusDeleted = z.infer<
  typeof VideoRenderVimeoStatusDeleted
>;
export type VideoRenderVimeoStatus = z.infer<typeof VideoRenderVimeoStatus>;
export type VideoRenderVimeoInfo = z.infer<typeof VideoRenderVimeoInfo>;
export type VideoRenderShareStatus = z.infer<typeof VideoRenderShareStatus>;
export type VideoRenderShareInfo = z.infer<typeof VideoRenderShareInfo>;
export type VideoRenderLocalInfo = z.infer<typeof VideoRenderLocalInfo>;
export type VideoRenderRemoteInfo = z.infer<typeof VideoRenderRemoteInfo>;
export type VideoRenderRenderInfo = z.infer<typeof VideoRenderRenderInfo>;
export type CreateVideoRenderDto = z.infer<typeof CreateVideoRenderDto>;
export type DeleteVideoRenderFromVimeoResponseDto = z.infer<
  typeof DeleteVideoRenderFromVimeoResponseDto
>;
export type VideoRenderExportType = z.infer<typeof VideoRenderExportType>;
export type VideoRenderDto = z.infer<typeof VideoRenderDto>;
